import type { TenantUsageSummaryResponse } from '@contract-analyzer/api-client';

import { formatShortDate } from '@/lib/format/date';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Inside this many days of the deadline the trial becomes decision-relevant,
 * so surfaces switch from a calendar date to a countdown and start pointing
 * at the charge-code path. Outside it, a trial is just a fact about the
 * workspace and nothing asks for attention.
 */
const TRIAL_FINAL_WEEK_DAYS = 7;

/**
 * Where an admin-granted trial stands, derived once from the usage summary
 * every member can read. `none` covers workspaces that never had a trial and
 * workspaces a charge code now funds, which the server reports by withholding
 * the deadline.
 */
export type TrialPhase =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'active';
      readonly endsAt: Date;
      /** Whole days until the deadline, never below 1 while the trial runs. */
      readonly daysLeft: number;
      readonly isFinalWeek: boolean;
      /** The deadline falls on today's calendar date in the viewer's time zone. */
      readonly isLastDay: boolean;
    }
  | { readonly kind: 'ended'; readonly endedAt: Date };

function isSameLocalDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function deriveTrialPhase(
  usage: Pick<TenantUsageSummaryResponse, 'trial_expires_at'>,
  now: Date,
): TrialPhase {
  if (usage.trial_expires_at === null || usage.trial_expires_at === undefined) {
    return { kind: 'none' };
  }
  const deadline = new Date(usage.trial_expires_at);
  if (Number.isNaN(deadline.getTime())) {
    return { kind: 'none' };
  }
  const remainingMs = deadline.getTime() - now.getTime();
  if (remainingMs <= 0) {
    return { kind: 'ended', endedAt: deadline };
  }
  const daysLeft = Math.max(1, Math.ceil(remainingMs / DAY_MS));
  return {
    kind: 'active',
    endsAt: deadline,
    daysLeft,
    isFinalWeek: daysLeft <= TRIAL_FINAL_WEEK_DAYS,
    // "Today" is a calendar claim: a deadline at 11:00 tomorrow is not today
    // even when fewer than 24 hours remain.
    isLastDay: isSameLocalDay(deadline, now),
  };
}

/** Fire a second past a boundary so every clock involved agrees it has passed. */
const BOUNDARY_GRACE_MS = 1000;

function msUntilNextLocalMidnight(now: Date): number {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return midnight.getTime() - now.getTime();
}

/**
 * When a surface showing this phase should recompute it, so an open tab never
 * shows a stale trial. The phase is derived once from a usage load, so it has
 * to be refreshed at each moment its display changes: the next whole-day step
 * of the countdown (`daysLeft`, and with it the final-week entry), the next
 * local midnight (`isLastDay` is a calendar claim), and a moment past the
 * deadline itself. `null` when there is nothing time-bound to wait for.
 */
export function trialRefreshDelayMs(phase: TrialPhase, now: Date): number | null {
  if (phase.kind !== 'active') {
    return null;
  }
  const untilDeadline = Math.max(phase.endsAt.getTime() - now.getTime(), 0);
  // `daysLeft` counts whole days back from the deadline, so it steps down
  // wherever the remaining time crosses a multiple of a day, not at midnight.
  const untilNextDayStep = untilDeadline % DAY_MS || DAY_MS;
  const next = Math.min(untilDeadline, untilNextDayStep, msUntilNextLocalMidnight(now));
  return next + BOUNDARY_GRACE_MS;
}

/** "24 Sep", or "24 Sep 2027" when the date falls outside the current year. */
export function formatTrialDate(date: Date, now: Date): string {
  return formatShortDate(date, now);
}


/**
 * The short value for a "Trial ends" row: a date while the end is far off, a
 * countdown once it is close, "today" on the last day.
 */
export function formatTrialEndsValue(
  phase: Extract<TrialPhase, { kind: 'active' }>,
  now: Date,
): string {
  if (phase.isLastDay) {
    return 'today';
  }
  if (phase.isFinalWeek) {
    return phase.daysLeft === 1 ? 'in 1 day' : `in ${String(phase.daysLeft)} days`;
  }
  return formatTrialDate(phase.endsAt, now);
}
